import React, { useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import axiosInstance from '../../../config/axios.config';
import { useReportService, useInvalidateReportService } from '../../../hooks/queries/useReportService';
import GenericSurepassOutputComponent from '../output/GenericSurepassOutputComponent';
import ConsentCheckbox from './ConsentCheckbox';
import ReportBanner from '../ReportBanner';

const FuelPriceStateListInputComponent = () => {
    const { serviceId } = useParams();
    const toast = useRef(null);
    const submittingRef = useRef(false);

    const [formData, setFormData] = useState({ consent: false });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState(null);
    // #161: one shared, cached query instead of a per-component fetch on every
    // mount. Concurrent consumers are deduped into a single request and a
    // revisit is served from cache; the balance is invalidated after a report
    // runs (below), which is what keeps a cached credit figure correct.
    const {
        data: fetchedServiceDetails,
        isFetching: fetchingService,
        isError: serviceError,
    } = useReportService(serviceId);
    const invalidateReportService = useInvalidateReportService();
    const serviceDetails = serviceError
        ? { available_credits: 'N/A', credits_required: 'N/A' }
        : (fetchedServiceDetails || null);

    const handleConsentChange = (e) => {
        setFormData({ consent: e.checked });
        if (errors.consent) setErrors((prev) => ({ ...prev, consent: '' }));
    };

    const submit = async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get(`/surepass/utilities/fuel-price/state-list?api_provider_service_id=${parseInt(serviceId, 10)}`);
            if (!res.data.success) throw new Error(res.data.message || 'Fetch failed');
            setReportData(res.data);
            toast.current?.show({ severity: 'success', summary: 'State list fetched', detail: 'Report ready below', life: 2000 });
            // Credits just changed. Invalidating updates every mounted consumer at
            // once and is ordered strictly after the deduction.
            invalidateReportService(serviceId);
        } catch (error) {
            console.error('Fuel state-list fetch failed:', error?.message || 'Unknown error');
            const status = error.response?.status;
            const map = { 500: 'Internal server error. Please try again later.', 429: 'Too many requests. Please wait a minute before trying again.' };
            let detail = map[status] || error.response?.data?.message || error.message || 'Fetch failed';
            if (status === 402) {
                const c = error.response?.data || {};
                detail = `Insufficient credits. Required: ${c.required_credits ?? 'N/A'}, Available: ${c.current_balance ?? 'N/A'}`;
            }
            toast.current?.show({ severity: 'error', summary: 'Fetch failed', detail, life: 3500 });
        } finally {
            setLoading(false);
            submittingRef.current = false;
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (submittingRef.current) return;
        if (!formData.consent) {
            setErrors({ consent: 'Please confirm consent before submitting' });
            toast.current?.show({ severity: 'warn', summary: 'Validation error', detail: 'Please confirm consent', life: 3000 });
            return;
        }
        setErrors({});
        submittingRef.current = true;
        confirmDialog({
            message: <div className="text-sm">Fetch the list of states supported for fuel-price lookups.</div>,
            header: 'Fetch fuel state list',
            icon: 'pi pi-question-circle text-primary pi-2x',
            acceptLabel: 'Yes, fetch',
            rejectLabel: 'Cancel',
            accept: submit,
            reject: () => { submittingRef.current = false; },
            style: { width: '90vw', maxWidth: '450px' },
            draggable: false,
        });
    };

    const handleAnother = () => {
        setReportData(null);
        setFormData({ consent: false });
        setErrors({});
    };

    return (
        <>
            <Toast ref={toast} />
            <ConfirmDialog />
            <div className="grid">
                <div className="col-12">
                    <ReportBanner
                        icon="pi-list"
                        title="Fuel Price State List"
                        subtitle="Fetch the list of states supported for fuel-price lookups"
                        availableCredits={serviceDetails?.available_credits}
                        requiredCredits={serviceDetails?.credits_required}
                        loading={fetchingService}
                    />
                    {!reportData ? (
                        <Card>
                            <form onSubmit={handleSubmit} noValidate>
                                <ConsentCheckbox checked={formData.consent} onChange={handleConsentChange} error={errors.consent} reportType="fuel-price-state-list" />
                                <div className="flex justify-content-end mt-3">
                                    <Button type="submit" label="Fetch State List" icon="pi pi-arrow-right" iconPos="right" loading={loading} disabled={loading} size="small" />
                                </div>
                            </form>
                        </Card>
                    ) : (
                        <>
                            <div className="flex justify-content-end mb-3">
                                <Button label="Refresh" icon="pi pi-refresh" severity="secondary" outlined size="small" onClick={handleAnother} />
                            </div>
                            <GenericSurepassOutputComponent reportData={reportData} title="Fuel Price State List" icon="pi-list" />
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default FuelPriceStateListInputComponent;
